/* Atlas v3 — The Agent Desk: the signed-in user's agentic Robinhood account,
   mirrored from Supabase (agent_holdings + agent_decisions). Three zones:
   account header (equity / buying power / synced staleness), holdings table,
   and the decision log below (reverse-chron, collapsible reasoning).

   Renders MIRROR DATA ONLY — no Robinhood calls anywhere. Data comes from
   useAgentPortfolio() (Clerk-auth'd, per-user; surfaces error). Tap a holding
   or decision row to open the underlying's TickerSheet.

   Built from the Leverage Desk idiom: brings its own shell (TickerStrip +
   BottomNav), one shared GRID reused by the holdings header + rows, explicit
   loading / empty / error branches. Reached from the More sheet (mobile). */
import { useEffect, useRef, useState } from 'react'
import { TickerCtx } from './lib'
import { StockLogo } from '@/v2/components/StockLogo'
import { TickerStrip, BottomNav, SearchPill, DeskSwitcher, MY_STOCKS_DESKS } from './shell'
import { StockDetailProvider, useStockDetail } from '@/v2/lib/StockDetailProvider'
import { useAgentPortfolio } from '@/v2/hooks/useAgentPortfolio'
import { useAgentConfig } from '@/v2/hooks/useAgentConfig'
import { AgentAccess } from '@/v2/components/AgentAccess'
import { AgentConfigSheet, GearButton, HaltLine } from '@/v2/components/agentconfig/AgentConfigPanel'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const Sans = { fontFamily: 'var(--studio-font-sans)' }
const Display = { fontFamily: 'var(--studio-font-display)', fontStyle: 'italic' }
const NUM = { ...Mono, fontVariantNumeric: 'tabular-nums' }

/* ── formatters ── */
function fmtMoney(v, digits = 2) {
  if (v == null || !Number.isFinite(v)) return '—'
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`
}
function fmtSigned(v, digits = 2) {
  if (v == null || !Number.isFinite(v)) return '—'
  const a = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })
  return `${v >= 0 ? '+' : '−'}$${a}`
}
function fmtQty(v) {
  if (v == null || !Number.isFinite(v)) return '—'
  return Number.isInteger(v) ? String(v) : v.toLocaleString('en-US', { maximumFractionDigits: 4 })
}
function relTime(iso) {
  if (!iso) return null
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return null
  const secs = Math.round((Date.now() - then) / 1000)
  if (secs < 60) return 'just now'
  const mins = Math.round(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.round(hrs / 24)
  return `${days}d ago`
}
function unrealized(h) {
  if (h.last_price == null || h.avg_cost == null || h.qty == null) return null
  return (h.last_price - h.avg_cost) * h.qty
}
function dirColor(v) {
  return v == null ? '--studio-text-subtle' : v >= 0 ? '--studio-success' : '--studio-danger'
}

/* shared holdings column grid — logo · ticker/qty · avg · last · mkt val · P&L.
   The header row and every data row use this so the columns line up exactly. */
const GRID = {
  display: 'grid',
  gridTemplateColumns: '22px minmax(44px, 1fr) 50px 50px 58px 58px',
  alignItems: 'center',
  gap: 8,
}
const HDR = { ...Mono, fontSize: 8.5, letterSpacing: '0.12em', color: 'hsl(var(--studio-text-subtle))', textTransform: 'uppercase' }
const cap = { ...Mono, fontSize: 11, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))', padding: '40px 20px', textAlign: 'center' }

/* ── one holdings row ── */
function HoldingRow({ h, onOpen }) {
  const [hot, setHot] = useState(false)
  const pnl = unrealized(h)
  const pnlCol = dirColor(pnl)
  return (
    <button onClick={() => onOpen(h.ticker)}
      onPointerDown={() => setHot(true)} onPointerUp={() => setHot(false)} onPointerCancel={() => setHot(false)} onPointerLeave={() => setHot(false)}
      style={{
        ...GRID, width: '100%', padding: '12px 20px', cursor: 'pointer', textAlign: 'left', border: 'none',
        borderTop: '1px solid hsl(var(--studio-border) / 0.4)',
        background: hot ? 'hsl(var(--studio-surface-2) / 0.55)' : 'transparent', transition: 'background 140ms',
      }}>
      <StockLogo ticker={h.ticker} size={22} rounded="50%" />
      <div style={{ minWidth: 0 }}>
        <div style={{ ...Mono, fontSize: 12.5, color: 'hsl(var(--studio-text))' }}>{h.ticker}</div>
        <div style={{ ...Mono, fontSize: 9, color: 'hsl(var(--studio-text-subtle))', marginTop: 2 }}>×{fmtQty(h.qty)}</div>
      </div>
      <span style={{ ...NUM, fontSize: 10.5, color: 'hsl(var(--studio-text-muted))', textAlign: 'right' }}>{fmtMoney(h.avg_cost)}</span>
      <span style={{ ...NUM, fontSize: 10.5, color: 'hsl(var(--studio-text))', textAlign: 'right' }}>{fmtMoney(h.last_price)}</span>
      <span style={{ ...NUM, fontSize: 10.5, color: 'hsl(var(--studio-text))', textAlign: 'right' }}>{fmtMoney(h.market_value, 0)}</span>
      <span style={{ ...NUM, fontSize: 10.5, color: `hsl(var(${pnlCol}))`, textAlign: 'right' }}>{fmtSigned(pnl, 0)}</span>
    </button>
  )
}

/* ── one decision-log row (reverse-chron; reasoning is collapsible).
   Amber budget: PROPOSED is the row's ONLY amber object, plus the 2px
   conviction bar on the left edge (amber as ink — height scales with
   Atlas's conviction read, so the log scans in one pass). The rating is
   plain text in the meta line, not a pill that looks tappable. ── */
function DecisionRow({ d, onOpen }) {
  const [open, setOpen] = useState(false)
  const cd = d.conviction_delta
  // 2px bar, 10px floor, +2px per conviction point, 34px cap.
  const barH = Math.min(34, 10 + (cd != null ? Math.abs(cd) * 2 : 0))
  const meta = [
    d.atlas_rating ? d.atlas_rating.toUpperCase() : null,
    cd != null && cd !== 0 ? `CONVICTION ${cd >= 0 ? '+' : '−'}${Math.abs(cd)}` : null,
    relTime(d.created_at)?.toUpperCase() || null,
  ].filter(Boolean).join(' · ')
  return (
    <div style={{ position: 'relative', borderTop: '1px solid hsl(var(--studio-border) / 0.4)', padding: '13px 20px' }}>
      <span aria-hidden style={{ position: 'absolute', left: 0, top: 14, width: 2, height: barH, background: 'hsl(var(--studio-accent))' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <button onClick={() => onOpen(d.ticker)} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', minWidth: 0 }}>
          <StockLogo ticker={d.ticker} size={20} rounded="50%" />
          <span style={{ ...Mono, fontSize: 12.5, color: 'hsl(var(--studio-text))' }}>{d.ticker}</span>
        </button>
        <span style={{ ...Mono, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'hsl(var(--studio-text))' }}>{d.action}</span>
        {d.qty != null && <span style={{ ...NUM, fontSize: 10.5, color: 'hsl(var(--studio-text-muted))' }}>×{fmtQty(d.qty)}</span>}
        {d.price != null && <span style={{ ...NUM, fontSize: 10.5, color: 'hsl(var(--studio-text-subtle))' }}>@ {fmtMoney(d.price)}</span>}
        <span style={{ flex: 1 }} />
        {d.placed
          ? <span style={{ ...Mono, fontSize: 8, letterSpacing: '0.1em', color: 'hsl(var(--studio-success))' }}>PLACED</span>
          : <span style={{ ...Mono, fontSize: 8, letterSpacing: '0.1em', color: 'hsl(var(--studio-accent))', border: '1px solid hsl(var(--studio-accent) / 0.4)', borderRadius: 'var(--studio-radius-sm)', padding: '2px 5px' }}>PROPOSED</span>}
      </div>
      {meta && (
        <div style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.04em', color: 'hsl(var(--studio-text) / 0.55)', marginTop: 8 }}>{meta}</div>
      )}
      {d.reasoning && (
        <>
          <div style={{
            ...Sans, fontSize: 12, lineHeight: 1.5, color: 'hsl(var(--studio-text-muted))', marginTop: 8,
            ...(open ? {} : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
          }}>{d.reasoning}</div>
          {d.reasoning.length > 90 && (
            <button onClick={() => setOpen((o) => !o)} style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-accent-soft))', background: 'transparent', border: 'none', padding: '5px 0 0', cursor: 'pointer' }}>
              {open ? 'LESS —' : 'MORE +'}
            </button>
          )}
        </>
      )}
    </div>
  )
}

function AgentDeskBody() {
  const { openTicker, hasTicker } = useStockDetail()
  const { holdings, decisions, account, syncedAt, loading, error } = useAgentPortfolio()
  const agentCfg = useAgentConfig()
  const [cfgOpen, setCfgOpen] = useState(false)

  // Agents can trade anything (HD, LLY, …) — off-universe row taps get a
  // visible "NOT COVERED BY ATLAS" flash instead of the provider's silent
  // no-op. hasTicker null = universe still loading → stay silent.
  const [notCovered, setNotCovered] = useState(null)
  const flashTimer = useRef(null)
  useEffect(() => () => clearTimeout(flashTimer.current), [])
  const tryOpen = (t) => {
    const known = hasTicker(t)
    if (known) { openTicker(t); return }
    if (known === false) {
      setNotCovered(t)
      clearTimeout(flashTimer.current)
      flashTimer.current = setTimeout(() => setNotCovered(null), 2200)
    }
  }

  // Header prefers STORED account state (agent_account_state — written on
  // agent sync); position-derived sums are the fallback for accounts synced
  // before account state existed.
  const derivedEquity = holdings.reduce((s, h) => s + (h.market_value ?? (h.last_price != null && h.qty != null ? h.last_price * h.qty : 0) ?? 0), 0)
  const equity = account?.equity ?? (holdings.length ? derivedEquity : null)
  const buyingPower = account?.buying_power ?? null
  const totalPnl = holdings.reduce((s, h) => { const p = unrealized(h); return p == null ? s : s + p }, 0)
  const hasHoldings = holdings.length > 0
  const hasCash = (account?.cash ?? 0) > 0
  const synced = relTime(syncedAt)

  // Sync freshness dot: green under an hour, amber when stale, red on error.
  const syncAgeMin = syncedAt ? (Date.now() - new Date(syncedAt).getTime()) / 60000 : null
  const dotCol = error ? '--studio-danger' : (syncAgeMin != null && syncAgeMin < 60) ? '--studio-success' : '--studio-accent'
  const syncLabel = loading ? 'SYNCING…' : error ? 'SYNC FAILED' : synced ? synced.toUpperCase() : 'NOT SYNCED'

  return (
    <TickerCtx.Provider value={openTicker}>
      <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column',
        background: 'radial-gradient(140% 75% at 50% -10%, hsl(var(--studio-background)) 0%, hsl(var(--studio-overlay)) 100%)' }}>
        {/* ── zone 1 · masthead (title + sync state) → cluster tabs → equity ── */}
        <div style={{ flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px 12px', gap: 12 }}>
            <div style={{ ...Display, fontSize: 30, color: 'hsl(var(--studio-text))', lineHeight: 1, minWidth: 0 }}>The Agent Desk</div>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
              <span style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: `hsl(var(${dotCol}))`, flexShrink: 0 }} />
                {syncLabel}
              </span>
              <GearButton onClick={() => setCfgOpen(true)} />
              <SearchPill />
            </span>
          </div>
          <DeskSwitcher items={MY_STOCKS_DESKS} variant="tabs" />
          {agentCfg.config?.kill_switch && <HaltLine style={{ margin: '0 20px' }} />}

          <div style={{ padding: '16px 20px 0' }}>
            <div style={HDR}>Equity</div>
            <div style={{ ...NUM, fontSize: 36, lineHeight: 1.1, color: 'hsl(var(--studio-text))', marginTop: 2 }}>{equity != null ? fmtMoney(equity, 2) : '—'}</div>
            {/* one hairline-bounded row, two equal cells, shared baseline */}
            <div style={{ display: 'flex', marginTop: 12, borderTop: '1px solid hsl(var(--studio-border) / 0.4)', borderBottom: '1px solid hsl(var(--studio-border) / 0.4)' }}>
              <div style={{ flex: 1, padding: '10px 0' }}>
                <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'hsl(var(--studio-text) / 0.6)' }}>Buying power</div>
                <div style={{ ...NUM, fontSize: 16, color: `hsl(var(${buyingPower != null ? '--studio-text' : '--studio-text-subtle'}))`, marginTop: 4 }}>{buyingPower != null ? fmtMoney(buyingPower, 2) : '—'}</div>
              </div>
              <div style={{ flex: 1, padding: '10px 0 10px 16px', borderLeft: '1px solid hsl(var(--studio-border) / 0.4)' }}>
                <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'hsl(var(--studio-text) / 0.6)' }}>Unrealized P&amp;L</div>
                <div style={{ ...NUM, fontSize: 16, color: `hsl(var(${hasHoldings ? dirColor(totalPnl) : '--studio-text-subtle'}))`, marginTop: 4 }}>{hasHoldings ? fmtSigned(totalPnl, 0) : '—'}</div>
              </div>
            </div>
            {notCovered && (
              <div style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-accent))', marginTop: 8 }}>{notCovered} NOT COVERED BY ATLAS</div>
            )}
          </div>
        </div>

        <div className="atlas-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
          {/* ── zone 2 · holdings. The table (header included) exists only when
              there are rows — an empty account gets one status strip instead,
              and the Decision Log starts above the fold. ── */}
          {loading ? (
            <div style={cap}>LOADING ACCOUNT…</div>
          ) : error ? (
            <div style={cap}>COULD NOT REACH ACCOUNT — {String(error.message || error).toUpperCase()}</div>
          ) : !hasHoldings ? (
            <div style={{ ...Mono, fontSize: 10.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-muted))', padding: '14px 20px', borderBottom: '1px solid hsl(var(--studio-border) / 0.4)' }}>
              {hasCash ? `ALL CASH · ${fmtMoney(account.cash)}` : 'NO POSITIONS — ACCOUNT UNFUNDED'}
            </div>
          ) : (
            <>
              <div style={{ ...GRID, padding: '14px 20px 7px' }}>
                <span />
                <span style={HDR}>Position</span>
                <span style={{ ...HDR, textAlign: 'right' }}>Avg</span>
                <span style={{ ...HDR, textAlign: 'right' }}>Last</span>
                <span style={{ ...HDR, textAlign: 'right' }}>Mkt Val</span>
                <span style={{ ...HDR, textAlign: 'right' }}>P&amp;L</span>
              </div>
              {holdings.map((h) => <HoldingRow key={h.ticker} h={h} onOpen={tryOpen} />)}
            </>
          )}

          {/* setup lives where the empty state is: an account that has NEVER
              synced (no state row, no positions) gets the full Agent Access
              section inline — mint a key + the connect manual — instead of a
              dead end pointing at Settings. */}
          {!loading && !error && !account && !hasHoldings && (
            <div style={{ padding: '16px 20px 8px' }}>
              <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.18em', color: 'hsl(var(--studio-accent))', marginBottom: 10 }}>CONNECT YOUR AGENT</div>
              <AgentAccess />
            </div>
          )}

          {/* ── zone 3 · decision log (header off the amber budget — the only
              amber here is PROPOSED + the conviction bars) ── */}
          <div style={{ padding: '22px 20px 8px' }}>
            <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.14em', color: 'hsl(var(--studio-text) / 0.7)', textTransform: 'uppercase' }}>Decision Log</div>
            <div style={{ ...Sans, fontSize: 11, color: 'hsl(var(--studio-text-subtle))', marginTop: 4 }}>Most recent first · agent trade rationale</div>
          </div>
          {loading ? null : error ? (
            <div style={cap}>—</div>
          ) : decisions.length === 0 ? (
            <div style={cap}>NO DECISIONS LOGGED</div>
          ) : (
            decisions.map((d, i) => <DecisionRow key={d.order_id || `${d.ticker}-${d.created_at}-${i}`} d={d} onOpen={tryOpen} />)
          )}
          <div style={{ height: 14 }} />
        </div>

        <div style={{ flexShrink: 0, borderTop: '1px solid hsl(var(--studio-border) / 0.5)', background: 'hsl(var(--studio-background) / 0.72)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
          <TickerStrip />
        </div>
        <BottomNav />
        {cfgOpen && <AgentConfigSheet cfg={agentCfg} syncedEquity={equity} onClose={() => setCfgOpen(false)} />}
      </div>
    </TickerCtx.Provider>
  )
}

export function AgentDesk() {
  return (
    <div className="v3-root" style={{ height: 'var(--v3-vh, 100dvh)', display: 'flex', flexDirection: 'column' }}>
      <StockDetailProvider>
        <AgentDeskBody />
      </StockDetailProvider>
    </div>
  )
}
